/**
 * 텍스트에서 전화번호, 이메일, URL을 감지하여 파싱하는 유틸리티 함수
 *
 * 처리 순서:
 * 1. 전화번호 감지 및 검증 (날짜, 금액, 시간 등과 구분)
 * 2. 이메일 주소 감지 (전화번호와 겹치지 않도록)
 * 3. URL 감지 (이메일과 겹치지 않도록)
 * 4. 토큰 배열 생성 및 반환
 */

// ============================================================================
// 정규식 패턴 정의
// ============================================================================

/**
 * 전화번호 감지 정규식 (다국가 지원)
 * 실제 전화번호 형식만 감지하도록 엄격한 패턴 사용
 * 날짜, 예약번호 등과 구분하기 위해 명확한 전화번호 구조만 매칭
 *
 * 지원 형식:
 * - 한국 휴대폰: 010-1234-5678, 010-123-4567, 01012345678
 * - 한국 일반전화: 02-123-4567, 031-123-4567, 02-1234-5678
 * - 국제 형식: +82-10-1234-5678, +1-555-123-4567
 * - 괄호 형식: (02) 123-4567, (010) 1234-5678
 */
const PHONE_PATTERNS = [
  // 국제 형식: +국가코드-지역코드-번호
  /\+\d{1,3}[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,4}/g,
  // 한국 휴대폰
  /(010|011|016|017|018|019)[-.\s]?\d{3,4}[-.\s]?\d{4}/g,
  // 한국 일반전화
  /(0[2-9]|0\d{2})[-.\s]?\d{3,4}[-.\s]?\d{4}/g,
  // 괄호 형식
  /\(?(0[2-9]|0\d{2}|010|011|016|017|018|019)\)?[-.\s]?\d{3,4}[-.\s]?\d{4}/g,
];

/**
 * 하이픈 없는 전화번호 패턴 (별도 처리 필요)
 * 더 엄격한 검증이 필요하여 별도 패턴으로 분리
 */
const PHONE_WITHOUT_HYPHEN_PATTERNS = [
  // 한국 휴대폰: 01012345678 (정확히 11자리)
  /\b(010|011|016|017|018|019)\d{8}\b/g,
  // 한국 일반전화: 021234567 (9-10자리), 0311234567 (10-11자리)
  /\b0[2-9]\d{7,9}\b/g,
  /\b0\d{2}\d{7,8}\b/g,
];

/**
 * 이메일 주소 감지 정규식
 * 일반적인 이메일 형식: user@domain.com
 */
const EMAIL_REGEX =
  /[a-zA-Z0-9]+([._%+-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}/g;

/**
 * URL 감지 정규식
 * http://, https://, www.로 시작하거나 일반적인 도메인 패턴
 * 이메일 주소는 제외하기 위해 @가 포함되지 않은 패턴만 매칭
 */
const URL_REGEX =
  /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}[^\s<>"']*)/g;

// ============================================================================
// 유틸리티 함수들 (사용 전에 정의)
// ============================================================================

/**
 * 두 범위가 겹치는지 확인
 * @param {number} start1 - 첫 번째 범위 시작 인덱스
 * @param {number} end1 - 첫 번째 범위 종료 인덱스
 * @param {number} start2 - 두 번째 범위 시작 인덱스
 * @param {number} end2 - 두 번째 범위 종료 인덱스
 * @returns {boolean} 겹치는지 여부
 */
const isOverlapping = (start1, end1, start2, end2) => {
  return (
    (start1 >= start2 && start1 < end2) || (start2 >= start1 && start2 < end1)
  );
};

/**
 * 기존 매칭들과 겹치는지 확인
 * @param {Array} existingMatches - 기존 매칭 배열
 * @param {number} index - 확인할 매칭의 시작 인덱스
 * @param {number} length - 확인할 매칭의 길이
 * @returns {boolean} 겹치는지 여부
 */
const hasOverlapWithExisting = (existingMatches, index, length) => {
  return existingMatches.some((match) =>
    isOverlapping(
      index,
      index + length,
      match.index,
      match.index + match.content.length
    )
  );
};

/**
 * URL을 정규화하여 href 형식으로 변환
 * @param {string} url - 원본 URL
 * @returns {string} 정규화된 URL
 */
const normalizeUrl = (url) => {
  // www.로 시작하는 경우 https:// 추가
  if (url.startsWith("www.")) {
    return `https://${url}`;
  }
  // 프로토콜이 없는 경우 https:// 추가
  if (!url.match(/^https?:\/\//i)) {
    return `https://${url}`;
  }
  return url;
};

/**
 * 전화번호 매칭 객체 생성
 * @param {string} matchedText - 매칭된 텍스트
 * @param {string} digitsOnly - 숫자만 추출한 텍스트
 * @param {number} index - 매칭 위치
 * @returns {Object} 전화번호 매칭 객체
 */
const createPhoneMatch = (matchedText, digitsOnly, index) => {
  return {
    type: "phone",
    content: matchedText,
    index: index,
    endIndex: index + matchedText.length,
    href: `tel:${digitsOnly}`,
    length: matchedText.length,
    digitsOnly: digitsOnly,
  };
};

/**
 * 전화번호 검증을 위한 컨텍스트 정보
 * @typedef {Object} PhoneValidationContext
 * @property {string} matchedText - 매칭된 텍스트
 * @property {string} digitsOnly - 숫자만 추출한 텍스트
 * @property {number} index - 매칭 위치
 * @property {string} fullText - 전체 텍스트
 */

/**
 * 하이픈/공백이 있는 전화번호 유효성 검증
 * 날짜, 금액, 시간 등과 구분하여 실제 전화번호만 감지
 * @param {PhoneValidationContext} context - 검증 컨텍스트
 * @returns {boolean} 유효한 전화번호인지 여부
 */
const isValidPhoneNumber = ({ matchedText, index, fullText }) => {
  // 날짜 형식 제외: YYYY.MM.DD, YYYY-MM-DD 패턴
  const isDatePattern =
    /^\d{4}[-.\/]\d{1,2}[-.\/]\d{1,2}/.test(matchedText) ||
    /^\d{4}\.\d{2}\.\d{2}/.test(matchedText);

  // 금액 형식 제외: ₩, $, 원 등과 함께 사용되는 숫자
  const beforeText = fullText.substring(Math.max(0, index - 3), index);
  const hasCurrencySymbol = /[₩$€£¥원]/.test(beforeText);

  // 시간 형식 제외: HH:MM 형식
  const afterText = fullText.substring(
    index + matchedText.length,
    index + matchedText.length + 3
  );
  const isTimePattern =
    /^\d{1,2}:\d{2}/.test(matchedText) ||
    /:\d{2}$/.test(matchedText) ||
    /:\d{2}/.test(afterText);

  // 괄호 안의 숫자만 있는 경우 제외 (예: (1박), (금) 등)
  const isOnlyInParentheses = /^\(\d+\)$/.test(matchedText.trim());

  // 날짜와 관련된 패턴 제외: YYYY.MM.DD 형식의 일부
  const isPartOfDate = /^\d{4}\.\d{2}\.\d{2}/.test(
    fullText.substring(Math.max(0, index - 10), index + matchedText.length)
  );

  return (
    !isDatePattern &&
    !hasCurrencySymbol &&
    !isTimePattern &&
    !isOnlyInParentheses &&
    !isPartOfDate
  );
};

/**
 * 하이픈 없는 전화번호 유효성 검증 (더 엄격)
 * 하이픈이 없어서 더 많은 오탐지 가능성이 있으므로 더 엄격한 검증 필요
 * @param {PhoneValidationContext} context - 검증 컨텍스트
 * @returns {boolean} 유효한 전화번호인지 여부
 */
const isValidPhoneNumberWithoutHyphen = ({ digitsOnly, index, fullText }) => {
  // 한국 휴대폰: 010으로 시작하고 정확히 11자리
  const isKoreanMobile = /^(010|011|016|017|018|019)\d{8}$/.test(digitsOnly);

  // 한국 일반전화: 02는 9-10자리, 031 등은 10-11자리
  const isKoreanLandline =
    /^0[2-9]\d{7,9}$/.test(digitsOnly) || /^0\d{2}\d{7,8}$/.test(digitsOnly);

  // 전화번호 형식이 아니면 제외
  if (!isKoreanMobile && !isKoreanLandline) {
    return false;
  }

  // 금액 형식 제외
  const beforeText = fullText.substring(Math.max(0, index - 5), index);
  const hasCurrencySymbol = /[₩$€£¥원]/.test(beforeText);

  // 날짜 형식 제외
  const contextText = fullText.substring(
    Math.max(0, index - 15),
    Math.min(fullText.length, index + digitsOnly.length + 15)
  );
  const isPartOfDate =
    /\d{4}\.\d{2}\.\d{2}/.test(contextText) ||
    /\d{4}-\d{2}-\d{2}/.test(contextText);

  // 주소의 일부인지 확인 (예: 경춘로 640-8)
  const isPartOfAddress =
    /로\s*\d+/.test(beforeText) ||
    /길\s*\d+/.test(beforeText) ||
    /번지\s*\d+/.test(beforeText);

  // 예약번호 같은 긴 숫자열 제외: 컨텍스트 확인
  const hasPhoneContext = /(예약문의|연락처|전화|tel|phone|contact)/i.test(
    contextText
  );

  // 예약번호 패턴: 이름 뒤에 괄호 안의 숫자 (예: 위영민(235905451))
  const isInParenthesesAfterName =
    /[가-힣a-zA-Z]+\s*\(/.test(
      fullText.substring(Math.max(0, index - 20), index)
    ) &&
    /\)/.test(
      fullText.substring(
        index,
        Math.min(fullText.length, index + digitsOnly.length + 5)
      )
    );

  // 금액 뒤의 숫자 제외 (예: ₩105,070)
  const isAfterAmount = /[₩$€£¥원]\s*\d+/.test(beforeText);

  return (
    !hasCurrencySymbol &&
    !isPartOfDate &&
    !isPartOfAddress &&
    !isInParenthesesAfterName &&
    !isAfterAmount &&
    (hasPhoneContext || isKoreanMobile || isKoreanLandline)
  );
};

/**
 * 전화번호 패턴 매칭 및 후보 수집
 * @param {string} text - 검색할 텍스트
 * @param {Array<RegExp>} patterns - 정규식 패턴 배열
 * @param {Function} validator - 유효성 검증 함수 (PhoneValidationContext를 받음)
 * @returns {Array} 전화번호 후보 배열
 */
const collectPhoneCandidates = (text, patterns, validator) => {
  const candidates = [];

  patterns.forEach((pattern) => {
    pattern.lastIndex = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const matchedText = match[0];
      const digitsOnly = matchedText.replace(/\D/g, "");

      // 최소 7자리 이상, 최대 15자리 이하 (ITU-T E.164 표준)
      if (digitsOnly.length >= 7 && digitsOnly.length <= 15) {
        const context = {
          matchedText,
          digitsOnly,
          index: match.index,
          fullText: text,
        };

        if (validator(context)) {
          candidates.push(
            createPhoneMatch(matchedText, digitsOnly, match.index)
          );
        }
      }
    }
  });

  return candidates;
};

/**
 * 전화번호 후보들을 정렬하고 중복 제거
 * @param {Array} candidates - 전화번호 후보 배열
 * @returns {Array} 중복 제거된 전화번호 매칭 배열
 */
const deduplicatePhoneCandidates = (candidates) => {
  // 인덱스 순으로 정렬하고, 같은 위치에서는 더 긴 것을 우선
  candidates.sort((a, b) => {
    if (a.index !== b.index) {
      return a.index - b.index;
    }
    return b.length - a.length;
  });

  // 위치 기반 중복 제거: 겹치는 매칭 중 더 긴 것을 선택
  const usedRanges = [];
  const matches = [];

  candidates.forEach((candidate) => {
    const isOverlap = usedRanges.some((range) =>
      isOverlapping(candidate.index, candidate.endIndex, range.start, range.end)
    );

    if (!isOverlap) {
      usedRanges.push({
        start: candidate.index,
        end: candidate.endIndex,
      });
      matches.push({
        type: candidate.type,
        content: candidate.content,
        index: candidate.index,
        href: candidate.href,
      });
    }
  });

  return matches;
};

// ============================================================================
// 메인 파싱 함수
// ============================================================================

/**
 * 텍스트를 파싱하여 텍스트와 링크를 구분한 토큰 배열을 반환
 * @param {string} text - 파싱할 텍스트
 * @returns {Array} 토큰 배열 [{type: 'text'|'phone'|'email'|'url', content: string, href?: string}, ...]
 */
export const parseText = (text) => {
  if (!text || typeof text !== "string") {
    return [{ type: "text", content: text || "" }];
  }

  const matches = [];

  // 1. 전화번호 매칭 및 검증
  const phoneCandidatesWithHyphen = collectPhoneCandidates(
    text,
    PHONE_PATTERNS,
    isValidPhoneNumber
  );
  const phoneCandidatesWithoutHyphen = collectPhoneCandidates(
    text,
    PHONE_WITHOUT_HYPHEN_PATTERNS,
    isValidPhoneNumberWithoutHyphen
  );
  const phoneMatches = deduplicatePhoneCandidates([
    ...phoneCandidatesWithHyphen,
    ...phoneCandidatesWithoutHyphen,
  ]);
  matches.push(...phoneMatches);

  // 2. 이메일 매칭 (전화번호와 겹치지 않도록)
  EMAIL_REGEX.lastIndex = 0;
  let emailMatch;
  while ((emailMatch = EMAIL_REGEX.exec(text)) !== null) {
    if (
      !hasOverlapWithExisting(matches, emailMatch.index, emailMatch[0].length)
    ) {
      matches.push({
        type: "email",
        content: emailMatch[0],
        index: emailMatch.index,
        href: `mailto:${emailMatch[0]}`,
      });
    }
  }

  // 3. URL 매칭 (이메일과 겹치지 않도록)
  URL_REGEX.lastIndex = 0;
  let urlMatch;
  while ((urlMatch = URL_REGEX.exec(text)) !== null) {
    // @가 포함된 경우 이메일 주소이므로 제외
    if (urlMatch[0].includes("@")) {
      continue;
    }

    if (!hasOverlapWithExisting(matches, urlMatch.index, urlMatch[0].length)) {
      matches.push({
        type: "url",
        content: urlMatch[0],
        index: urlMatch.index,
        href: normalizeUrl(urlMatch[0]),
      });
    }
  }

  // 4. 인덱스 순으로 정렬
  matches.sort((a, b) => a.index - b.index);

  // 5. 토큰 배열 생성
  const tokens = [];
  let lastIndex = 0;

  matches.forEach((match) => {
    // 매칭 전 텍스트 추가
    if (match.index > lastIndex) {
      const textBefore = text.substring(lastIndex, match.index);
      if (textBefore) {
        tokens.push({ type: "text", content: textBefore });
      }
    }

    // 링크 토큰 추가
    tokens.push({
      type: match.type,
      content: match.content,
      href: match.href,
    });

    lastIndex = match.index + match.content.length;
  });

  // 마지막 매칭 이후 텍스트 추가
  if (lastIndex < text.length) {
    const textAfter = text.substring(lastIndex);
    if (textAfter) {
      tokens.push({ type: "text", content: textAfter });
    }
  }

  // 매칭이 없는 경우 전체 텍스트 반환
  if (tokens.length === 0) {
    tokens.push({ type: "text", content: text });
  }

  return tokens;
};
